// Type for tile data - can be expanded as needed
type TileValue = number | null;

// Interface for a tile with position and value
interface Tile {
    x: number;
    z: number;
    value: TileValue;
}

/**
 * WorldMap class for managing chunks of the game world
 * Handles loading, storing, and retrieving tile data with chunk-based optimization
 */
class WorldMap {
    private chunks: Map<string, TileValue[][]>;
    private chunkSize: number;

    constructor(chunkSize: number = 16) {
        this.chunks = new Map<string, TileValue[][]>(); // key format: "chunkX,chunkZ"
        this.chunkSize = chunkSize;
    }

    /**
     * Convert world coordinates to chunk coordinates
     * @param x - X coordinate in world space
     * @param z - Z coordinate in world space
     * @returns Chunk coordinates [chunkX, chunkZ]
     */
    getChunkCoords(x: number, z: number): [number, number] {
        const chunkX = Math.floor(x / this.chunkSize);
        const chunkZ = Math.floor(z / this.chunkSize);
        return [chunkX, chunkZ];
    }

    /**
     * Get local coordinates within a chunk
     * @param x - X coordinate in world space
     * @param z - Z coordinate in world space
     * @returns Local coordinates [localX, localZ] within the chunk
     */
    getLocalCoords(x: number, z: number): [number, number] {
        const localX = ((x % this.chunkSize) + this.chunkSize) % this.chunkSize;
        const localZ = ((z % this.chunkSize) + this.chunkSize) % this.chunkSize;
        return [localX, localZ];
    }

    /**
     * Set a tile at the specified coordinates
     * @param x - X coordinate in world space
     * @param z - Z coordinate in world space
     * @param value - Value to set at the tile position
     */
    setTile(x: number, z: number, value: TileValue): void {
        const [chunkX, chunkZ] = this.getChunkCoords(x, z);
        const chunkKey = `${chunkX},${chunkZ}`;
        
        if (!this.chunks.has(chunkKey)) {
            // Initialize new chunk with empty tiles (or default values)
            this.chunks.set(chunkKey, Array(this.chunkSize).fill(null)
                .map(() => Array(this.chunkSize).fill(null)));
        }

        const [localX, localZ] = this.getLocalCoords(x, z);
        const chunk = this.chunks.get(chunkKey)!;
        chunk[localX][localZ] = value;
    }

    /**
     * Get a tile at the specified coordinates
     * @param x - X coordinate in world space
     * @param z - Z coordinate in world space
     * @returns The value at the tile position or null if not found
     */
    getTile(x: number, z: number): TileValue {
        const [chunkX, chunkZ] = this.getChunkCoords(x, z);
        const [localX, localZ] = this.getLocalCoords(x, z);
        const chunk = this.chunks.get(`${chunkX},${chunkZ}`);
        return chunk ? chunk[localX][localZ] : null;
    }

    /**
     * Get all tiles within a rectangular area
     * @param xMin - Minimum X coordinate of the area
     * @param xMax - Maximum X coordinate of the area
     * @param zMin - Minimum Z coordinate of the area
     * @param zMax - Maximum Z coordinate of the area
     * @returns Array of tiles with their positions and values
     */
    getTilesInArea(xMin: number, xMax: number, zMin: number, zMax: number): Tile[] {
        const tiles: Tile[] = [];
        for (let x = xMin; x <= xMax; x++) {
            for (let z = zMin; z <= zMax; z++) {
                const tile = this.getTile(x, z);
                if (tile !== null) {
                    tiles.push({ x, z, value: tile });
                }
            }
        }
        return tiles;
    }

    /**
     * Check if a tile exists at the specified coordinates
     * @param x - X coordinate in world space
     * @param z - Z coordinate in world space
     * @returns True if the tile exists, false otherwise
     */
    hasTile(x: number, z: number): boolean {
        const [chunkX, chunkZ] = this.getChunkCoords(x, z);
        const chunk = this.chunks.get(`${chunkX},${chunkZ}`);
        
        if (!chunk) return false;
        
        const [localX, localZ] = this.getLocalCoords(x, z);
        const tile = chunk[localX][localZ];
        
        return tile !== null && tile !== undefined;
    }
}

export default WorldMap; 